import { createContext, useContext, useMemo, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

// Global /api/premium toggle removed — Premium is now strictly per-user
// (`user.tier === "premium"`). This provider is a thin lens over the
// authenticated session that keeps the historical export shape
// (`premium`, `refresh`, `setPremium`, `loading`, `usePremium`) intact
// so no other file needs to change.

const PremiumContext = createContext({
  premium: false,
  loading: false,
  refresh: () => {},
  setPremium: () => {},
});

export const PremiumProvider = ({ children }) => {
  const { user, loading, refresh: refreshSession } = useAuth();
  const premium = user?.tier === "premium";

  // `refresh` was previously a GET /api/premium. Now that the global
  // toggle is gone, refreshing means re-reading the session so the
  // per-user tier reflects any webhook / admin change.
  const refresh = useCallback(async () => {
    try { await refreshSession(); } catch { /* silent */ }
  }, [refreshSession]);

  // `setPremium` was previously a POST /api/premium. The global toggle
  // has been removed — per-user tier can only be changed by Stripe
  // webhooks or the admin API. This is kept as a no-op so existing
  // callers (e.g. Settings.jsx) don't crash.
  const setPremium = useCallback(async () => {
    // no-op: per-user tier only, no global toggle to write.
  }, []);

  const value = useMemo(
    () => ({ premium, loading, refresh, setPremium }),
    [premium, loading, refresh, setPremium],
  );

  return (
    <PremiumContext.Provider value={value}>
      {children}
    </PremiumContext.Provider>
  );
};

export const usePremium = () => useContext(PremiumContext);
